/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useState } from 'react'

import {
  EuiBadge,
  EuiButton,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiPanel,
  EuiSpacer,
  EuiTable,
  EuiTableBody,
  EuiTableHeader,
  EuiTableHeaderCell,
  EuiTableRow,
  EuiTableRowCell,
  EuiText,
  EuiTitle,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { useGetAuditLogsConfiguration } from '@modules/audit-logs-api'
import { useFlagsWhenLoaded } from '@modules/launchdarkly'
import { useConfig } from '@modules/cui/ConfigContext'

import SecurityDocsLink from '../SecurityDocsLink'

import ConfigureAuditTrailFlyout from './ConfigureAuditTrailFlyout'
import { DeploymentName } from './DeploymentName'

import type { FunctionComponent } from 'react'

const AuditTrail: FunctionComponent<{ readOnly?: boolean; organizationId: string }> = ({
  readOnly,
  organizationId,
}) => {
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false)
  const { data } = useGetAuditLogsConfiguration({ organizationId })
  const [isFlagReady, { auditLog }] = useFlagsWhenLoaded()
  const isAdminConsole = useConfig('APP_NAME') === 'adminconsole'

  const renderDestination = () => {
    if (data?.destination) {
      return (
        <Fragment>
          <EuiSpacer size='m' />
          <EuiTable>
            <EuiTableHeader>
              <EuiTableHeaderCell>
                <FormattedMessage
                  id='organization.security.audit-trail.destination-property'
                  defaultMessage='Destination'
                />
              </EuiTableHeaderCell>
              <EuiTableHeaderCell>
                <FormattedMessage
                  id='organization.security.audit-trail.destination-value'
                  defaultMessage='Index'
                />
              </EuiTableHeaderCell>
            </EuiTableHeader>
            <EuiTableBody>
              <EuiTableRow key='destination-type'>
                <EuiTableRowCell>
                  <DeploymentName deploymentId={data.destination.deployment_id} />
                </EuiTableRowCell>
                <EuiTableRowCell>{data.destination.index}</EuiTableRowCell>
              </EuiTableRow>
            </EuiTableBody>
          </EuiTable>
        </Fragment>
      )
    }

    return null
  }

  const renderButton = () => {
    if (data?.destination) {
      return (
        <EuiButton size='m' onClick={() => setIsFlyoutOpen(true)}>
          <FormattedMessage
            id='organization.security.audit-trail.manage-button'
            defaultMessage='Manage'
          />
        </EuiButton>
      )
    }

    return (
      <EuiButton size='m' onClick={() => setIsFlyoutOpen(true)}>
        <FormattedMessage
          id='organization.security.audit-trail.configure-button'
          defaultMessage='Configure'
        />
      </EuiButton>
    )
  }

  if (isAdminConsole || (isFlagReady && auditLog)) {
    return (
      <Fragment>
        <EuiPanel paddingSize='xl'>
          <EuiFlexGroup alignItems='center' gutterSize='m'>
            <EuiFlexItem grow={false}>
              <EuiIcon type='securityApp' size='l' />
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiFlexGroup responsive={false} alignItems='center' gutterSize='s'>
                <EuiFlexItem grow={false}>
                  <EuiTitle size='s'>
                    <h3>
                      <FormattedMessage
                        id='organization.security.audit-trail.title'
                        defaultMessage='Cloud audit trail'
                      />
                    </h3>
                  </EuiTitle>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiBadge color='accent'>
                    <FormattedMessage
                      id='organization.security.audit-trail.new-badge'
                      defaultMessage='NEW'
                    />
                  </EuiBadge>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>{!readOnly && renderButton()}</EuiFlexItem>
          </EuiFlexGroup>
          <EuiSpacer size='m' />
          <EuiText color='subdued' size='s'>
            <FormattedMessage
              id='organization.security.audit-trail.description'
              defaultMessage='Keep track of actions performed on your Elastic Cloud infrastructure by members of your organization. <lm>Learn more</lm>'
              values={{
                lm: (msg) => <SecurityDocsLink fragment='ec-audit-trail'>{msg}</SecurityDocsLink>,
              }}
            />
          </EuiText>
          {renderDestination()}
        </EuiPanel>

        {isFlyoutOpen && (
          <ConfigureAuditTrailFlyout
            onClose={() => setIsFlyoutOpen(false)}
            organizationId={organizationId}
            deploymentId={data?.destination?.deployment_id}
          />
        )}
      </Fragment>
    )
  }

  return null
}

export default AuditTrail
